import { useEffect, useState } from "react";
import { Button, colors } from "@cliqsuite/ui";
import { SettingsPanel } from "./SettingsPrimitives";

interface ReleaseEntry {
  version: string;
  url: string;
}
interface ReleasesManifest {
  windows?: ReleaseEntry;
  android?: ReleaseEntry;
}

/**
 * Fetches releases.json directly from MinIO's public URL — no sync-api
 * round trip, same as how every other MinIO-hosted asset in this app is
 * served (see services/sync-api/src/lib/storage.ts's public-read bucket
 * pattern). Publishing a release is a manual, developer-run step
 * (services/sync-api/scripts/publish-print-hub-release.ts); nothing here
 * writes to the bucket.
 */
export function usePrintHubReleases() {
  const storageUrl = import.meta.env.VITE_STORAGE_URL as string | undefined;
  const [manifest, setManifest] = useState<ReleasesManifest | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!storageUrl) return;
    let cancelled = false;
    fetch(`${storageUrl}/print-hub-releases/releases.json`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`${res.status}`))))
      .then((data: ReleasesManifest) => {
        if (!cancelled) setManifest(data);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storageUrl]);
  return { storageUrl, manifest, loadFailed };
}

export function PrintHubDownloads() {
  const { storageUrl, manifest, loadFailed } = usePrintHubReleases();

  return (
    <div className="owner-settings-stack">
      <SettingsPanel title="Download Print Hub" description="Install the companion app on a store device, then pair it from the Print Hubs page.">
        {!storageUrl ? (
          <div style={{ fontSize: 13, color: colors.textMuted }}>
            Downloads aren't configured for this deployment yet.
          </div>
        ) : loadFailed ? (
          <div style={{ fontSize: 13, color: colors.textMuted }}>
            No release has been published yet — check back soon.
          </div>
        ) : !manifest ? (
          <div style={{ fontSize: 13, color: colors.textMuted }}>Loading…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <DownloadRow label="Windows" entry={manifest.windows} icon="desktop_windows" />
            <DownloadRow label="Android" entry={manifest.android} icon="android" />
          </div>
        )}
      </SettingsPanel>
      <div className="owner-settings-print-note"><span className="ms" aria-hidden="true">security</span><div><strong>Local and secure</strong><p>Print Hub keeps receipt jobs on your store network and reconnects automatically after interruptions.</p></div></div>
    </div>
  );
}

function DownloadRow({ label, entry, icon }: { label: string; entry?: ReleaseEntry; icon: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: "#EAF0FB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        <span className="ms" style={{ fontSize: 20, color: "#2D64C8" }}>
          {icon}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>{label}</div>
        <div style={{ fontSize: 11.5, color: colors.textMuted, fontWeight: 600 }}>
          {entry ? `Version ${entry.version}` : "Not published yet"}
        </div>
      </div>
      {entry && (
        <a href={entry.url} download>
          <Button variant="secondary" style={{ padding: "8px 14px", fontSize: 12.5 }}>
            Download
          </Button>
        </a>
      )}
    </div>
  );
}
